import { FC, useState, useMemo } from 'react';
import cn from 'classnames';
// Types
import { ContactType, TargetType } from 'api/graphql-global-types';
// Helpers
import {
  getMentionLabel,
  getMentionAvatarUrl,
  getMentionTitle,
  MentionData,
  sortByRegistered,
} from 'helpers/mentions';
// Components
import FollowButton from 'components/FollowButton/FollowButton';
import MillionsIcon from 'ui3/MillionsIcon/MillionsIcon';
import Modal from 'ui3/Modal/Modal';
// Styles
import styles from './MentionsList.module.scss';

type MentionsListProps = {
  mentions: MentionData[];
  onClose: () => void;
};

type TabType = 'athletes' | 'brands';

const MentionsList: FC<MentionsListProps> = ({ mentions, onClose }) => {
  const [activeTab, setActiveTab] = useState<TabType>('athletes');

  const { athletes, brands } = useMemo(() => {
    const athletesList: MentionData[] = [];
    const brandsList: MentionData[] = [];

    mentions.forEach((mention) => {
      const isBrand = mention.unregisteredContact?.type === ContactType.BRAND;

      if (isBrand) {
        brandsList.push(mention);
      } else {
        athletesList.push(mention);
      }
    });

    return {
      athletes: athletesList.sort(sortByRegistered),
      brands: brandsList.sort(sortByRegistered),
    };
  }, [mentions]);

  const renderMentionItem = (mention: MentionData) => {
    const label = getMentionLabel(mention);
    const avatarUrl = getMentionAvatarUrl(mention);
    const title = getMentionTitle(mention);
    const isRegistered = mention.targetType === TargetType.REGISTERED;

    return (
      <div key={mention.id} className={styles.mentionItem}>
        <div className={styles.avatarWrapper}>
          <img src={avatarUrl} alt={label} className={styles.avatar} />
        </div>
        <div className={styles.detailsWrapper}>
          <div className={styles.nameRow}>
            <span className={styles.name}>{label}</span>
            <MillionsIcon
              name={isRegistered ? 'verified' : 'outlineQuestionMark'}
              className={cn(styles.verificationIcon, {
                [styles.registered]: isRegistered,
                [styles.unregistered]: !isRegistered,
              })}
              size={16}
            />
          </div>
          <span className={styles.title}>{title}</span>
        </div>
        {isRegistered && mention.store && (
          <FollowButton
            store={mention.store as any}
            unfollowText="Following"
            className={styles.followButton}
            xs="small"
          />
        )}
      </div>
    );
  };

  const displayedMentions = activeTab === 'athletes' ? athletes : brands;

  return (
    <Modal
      open={true}
      onClose={onClose}
      size="large"
      blockScroll={true}
      showCloseIcon={true}
      className={styles.modal}
    >
      <div className={styles.modalContent}>
        <div className={styles.header}>
          <h2 className={styles.modalTitle}>Mentions</h2>
        </div>

        <div className={styles.tabs}>
          <button
            type="button"
            className={cn(styles.tab, {
              [styles.activeTab]: activeTab === 'athletes',
            })}
            onClick={() => setActiveTab('athletes')}
          >
            Athletes
          </button>
          <button
            type="button"
            className={cn(styles.tab, {
              [styles.activeTab]: activeTab === 'brands',
            })}
            onClick={() => setActiveTab('brands')}
          >
            Brands
          </button>
        </div>

        <div className={styles.mentionsList}>
          {displayedMentions.length > 0 ? (
            displayedMentions.map(renderMentionItem)
          ) : (
            <div className={styles.emptyState}>
              <p>No {activeTab} found</p>
            </div>
          )}
        </div>
      </div>
    </Modal>
  );
};

export default MentionsList;
